import React, { useState, useEffect, useContext } from 'react'
import { IconButton, TextField, Divider, debounce } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { GridColDef, GridPaginationModel, GridRowParams, GridValueGetterParams, ptBR } from '@mui/x-data-grid'
import { Header, Content, Image, BotaoDelete, StyledDataGrid, NotaFiscalStatusIcon } from './styles'
import axios from '../../../services/withAxios';
import VendasIcon from '../../../assets/vendas-icon.png'
import Venda from '../../../interfaces/venda'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { GFLContext } from '../../../infra/GFLContext'
import DownloadVendaProps from '../../../interfaces/downloadVendaProps'
import VendaListagem from '../../../interfaces/vendaListagem'
import { DownloadVenda } from '../../../services/DownloadVenda'
import DeleteIcon from '@mui/icons-material/Delete'
import { useConfirm } from 'material-ui-confirm'
import { EsconderPorNivelAcesso } from '../../../services/auth'
import { NivelAcessoEnum } from '../../../utils/enum/nivelAcessoEnum'
import { TypeAlertEnum } from '../../../utils/enum/typeAlertEnum'
import VendaDialog from '../../components/VendaDialog'
import { isStatusCanceladoNotaFiscal, isStatusSucessoNotaFiscal } from '../../../utils/Constants'
import pagedGet from '../../../services/pagedGet'
import { DEFAULT_PAGE_SIZE } from '../../../interfaces/pagedResult'

const Vendas: React.FC = () => {
  const [pesquisa, setPesquisa] = useState<string>('')
  const [vendas, setVendas] = useState<VendaListagem[]>([])
  const [vendaSelecionada, setVendaSelecionada] = useState<VendaListagem>(null);
  const [pesquisarTrigger, setPesquisarTrigger] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [rowCount, setRowCount] = useState(0)

  const contexto = useContext(GFLContext)
  const confirm = useConfirm()

  const pesquisar = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    setPesquisa(event.target.value);
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, 500)

  useEffect(() => {
    if (vendaSelecionada)
      return

    const buscarDados = async () => {
      const result = await pagedGet<VendaListagem>('/venda', {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        pesquisa: pesquisa || undefined,
      })
      setVendas(result.items)
      setRowCount(result.totalCount)
    }

    buscarDados()
  }, [pesquisa, pesquisarTrigger, vendaSelecionada, paginationModel])

  const handleDownloadVenda = (item: VendaListagem) => {
    axios.get(`/venda/${item.id}`)
      .then((response) => {
        const venda = response.data as Venda
        const downloadProps: DownloadVendaProps = {
          cliente: {
            cpf: venda.clienteCPF,
            nome: venda.clienteNome,
            observacao: venda.clienteObservacao,
            telefone: venda.clienteTelefone,
            email: venda.clienteEmail
          },
          formaPagamento: venda.formaPagamento,
          desconto: venda.valorDesconto,
          valorTotal: item.valorTotal,
          dataVenda: item.data,
          vendaItens: venda.itens,
          contexto: contexto
        }

        try {
          DownloadVenda(downloadProps)
        } catch (e: any) {
          // datadogLogs.logger.error(`Erro ao imprimir venda`, e);
          contexto.setMessageAlert(`Houve um problema ao imprimir a venda. Log: ${e}`)
          contexto.setTypeAlert(TypeAlertEnum.error)
          contexto.setOpenAlert(true)
        }
      })
  }

  const handleDeleteVenda = (item: VendaListagem) => {
    confirm({ description: 'Excluir permanentemente?', title: 'Tem certeza?', confirmationButtonProps: { autoFocus: true } })
      .then(() => {
        axios.delete(`/venda/${item.id}`).then(() => { setPesquisarTrigger(!pesquisarTrigger) })
      });
  }

  const handleClickLinha = (params: GridRowParams) => {
    setVendaSelecionada(params.row as VendaListagem)
  }

  const handleOnClose = () => {
    setVendaSelecionada(null);
  }

  const getNotaFiscalStatusIcon = (status: number) => {
    let title = '';
    let backgroundColor = '';

    if (isStatusSucessoNotaFiscal(status)) {
      title = 'Nota Fiscal emitida com sucesso'
      backgroundColor = '#4caf50'
    } else if (isStatusCanceladoNotaFiscal(status)) {
      title = 'Nota Fiscal cancelada'
      backgroundColor = 'orange'
    } else if (status > 0 && status !== 100) {
      title = 'Erro na emissão da Nota Fiscal'
      backgroundColor = '#f44336'
    } else {
      title = 'Nota Fiscal pendente'
      backgroundColor = '#9e9e9e'
    }

    return <NotaFiscalStatusIcon backgroundColor={backgroundColor} title={title} />
  }

  const colunas: GridColDef[] = [
    {
      field: 'clienteNome',
      headerName: 'Cliente',
      width: 300,
      valueGetter: (getter: GridValueGetterParams) => {
        return getter.value === null ? '-' : getter.value
      }
    },
    {
      field: 'data',
      headerName: 'Data',
      flex: 1,
      valueGetter: (data: GridValueGetterParams<Date, any>) => {
        return new Date(data.value).toLocaleString().slice(0, -3);
      }
    },
    {
      field: 'valorDesconto',
      headerName: 'Desconto',
      flex: 1,
      valueFormatter: (params: any) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(params.value)
      }
    },
    {
      field: 'valorTotal',
      headerName: 'Total',
      flex: 1,
      valueFormatter: (params: any) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(params.value)
      },
    },
    {
      field: 'notaFiscalStatus',
      headerName: 'NFC-e',
      width: 80,
      align: 'center',
      renderCell: (cellValues) => {
        return getNotaFiscalStatusIcon(cellValues.value as number);
      },
      sortable: false
    },
    {
      field: 'download',
      headerName: '',
      width: 100,
      align: 'center',
      renderCell: (cellValues) => {
        return (
          <>
            <IconButton color="success" onClick={() => handleDownloadVenda(cellValues.row as VendaListagem)} component="span">
              <FileDownloadIcon />
            </IconButton>
            <Divider orientation="vertical" variant="middle" flexItem />
            <EsconderPorNivelAcesso nivelAcesso={[NivelAcessoEnum.admin, NivelAcessoEnum.gerente]}>
              <>
                <BotaoDelete color="error" onClick={() => handleDeleteVenda(cellValues.row as VendaListagem)} component="span">
                  <DeleteIcon />
                </BotaoDelete>
              </>
            </EsconderPorNivelAcesso>
          </>
        );
      }
    }
  ]

  return (
    <>
      <Image src={VendasIcon} />
      <Header>
        <TextField
          id="pesquisa"
          label="Pesquisar"
          onChange={pesquisar}
          type="search"
          fullWidth
          autoFocus
          autoComplete='off'
          inputProps={{ style: { textTransform: "uppercase" } }}
        />
        <IconButton
          color="primary"
          onClick={_ => setPesquisarTrigger(!pesquisarTrigger)}
        >
          <SearchIcon />
        </IconButton>
      </Header>
      <Content>
        <StyledDataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          columns={colunas}
          rows={vendas}
          disableColumnMenu={true}
          onRowClick={handleClickLinha}
          hideFooterSelectedRowCount={true}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          pageSizeOptions={[20, 50, 100]}
          sx={{
            textTransform: 'uppercase'
          }}
        />
      </Content>
      {vendaSelecionada ? <VendaDialog aberto={false} setAberto={handleOnClose} entidade={vendaSelecionada} /> : <></>}
    </>
  )
}

export default Vendas